import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useNavigate } from "react-router";
import { toast } from "sonner";
import { Activity, Trash2, UserPlus } from "lucide-react";
import { useAdminAuth } from "@/lib/hooks/useAdminAuth";
import { getStoreMembers, adminRemoveMember } from "@/lib/api/admin";
import type { StoreMember } from "@/lib/api/types";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Spinner } from "@/components/Spinner";
import { EmptyState } from "@/components/EmptyState";
import { ConfirmDialog } from "@/components/ConfirmDialog";
import { StaffActivityDialog } from "../StaffActivityDialog";
import { AddMemberDialog } from "../AddMemberDialog";
import { formatDate } from "@/lib/utils/date";

type StoreStaffTabProps = {
  storeId: string;
};

export function StoreStaffTab({ storeId }: StoreStaffTabProps) {
  const user = useAdminAuth();
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const [selectedStaff, setSelectedStaff] = useState<{ id: string; name: string } | null>(null);
  const [staffToRemove, setStaffToRemove] = useState<StoreMember | null>(null);
  const [addOpen, setAddOpen] = useState(false);

  const { data: members, isLoading } = useQuery({
    queryKey: ["store-members", storeId],
    queryFn: async () => getStoreMembers(user!, storeId),
    enabled: !!user,
    refetchOnMount: "always",
  });

  // Firebase-auth staff only (real UID, not legacy "staff:<id>")
  const staff =
    members?.filter((m) => m.role === "staff" && !m.user_uid.startsWith("staff:")) ?? [];

  const removeMutation = useMutation({
    mutationFn: (memberId: string) => adminRemoveMember(user!, storeId, memberId),
    onSuccess: () => {
      toast.success("Staff member removed");
      void queryClient.invalidateQueries({ queryKey: ["store-members", storeId] });
      setStaffToRemove(null);
    },
    onError: (err: Error) => {
      toast.error(err.message);
      setStaffToRemove(null);
    },
  });

  if (isLoading) {
    return (
      <div className="flex justify-center py-12">
        <Spinner className="size-5 border-primary" />
      </div>
    );
  }

  return (
    <>
      <div className="space-y-4">
        <div className="flex items-center justify-between">
          <p className="text-sm text-muted-foreground">
            {staff.length === 0
              ? "No staff members yet."
              : `${staff.length} staff member${staff.length === 1 ? "" : "s"}`}
          </p>
          <Button size="sm" variant="secondary" onClick={() => setAddOpen(true)}>
            <UserPlus className="size-4 mr-1.5" />
            Add Staff
          </Button>
        </div>

        {staff.length === 0 ? (
          <EmptyState
            icon={UserPlus}
            title="No staff members"
            description="Add a platform user as staff to give them scanner access to this store."
          />
        ) : (
          <div className="space-y-2">
            {staff.map((m) => (
              <Card key={m.id}>
                <CardContent className="flex items-center justify-between gap-3 p-3">
                  <div className="min-w-0">
                    <button
                      type="button"
                      className="font-medium text-left hover:underline underline-offset-2 truncate block text-sm"
                      onClick={() => void navigate(`/users/${m.user_uid}`)}
                    >
                      {m.first_name} {m.last_name}
                    </button>
                    <p className="text-xs text-muted-foreground">
                      Since {formatDate(m.created_at)}
                    </p>
                  </div>
                  <div className="flex items-center gap-1 shrink-0">
                    <Button
                      variant="ghost"
                      size="icon"
                      className="size-7"
                      onClick={() =>
                        setSelectedStaff({ id: m.user_uid, name: `${m.first_name} ${m.last_name}` })
                      }
                      aria-label={`View ${m.first_name} ${m.last_name}'s scanner activity`}
                    >
                      <Activity className="size-3.5" />
                    </Button>
                    <Button
                      variant="ghost"
                      size="icon"
                      className="size-7 text-muted-foreground enabled:hover:text-destructive enabled:hover:bg-destructive/10"
                      onClick={() => setStaffToRemove(m)}
                      aria-label={`Remove ${m.first_name} ${m.last_name}`}
                    >
                      <Trash2 className="size-3.5" />
                    </Button>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </div>

      <StaffActivityDialog staff={selectedStaff} onClose={() => setSelectedStaff(null)} />

      <AddMemberDialog open={addOpen} onOpenChange={setAddOpen} storeId={storeId} role="staff" />

      <ConfirmDialog
        open={!!staffToRemove}
        onOpenChange={(open) => {
          if (!open) setStaffToRemove(null);
        }}
        title="Remove staff member"
        description={
          staffToRemove
            ? `Remove ${staffToRemove.first_name} ${staffToRemove.last_name} from this store? They will lose scanner access.`
            : ""
        }
        confirmLabel="Remove"
        destructive
        isPending={removeMutation.isPending}
        onConfirm={() => {
          if (staffToRemove) removeMutation.mutate(staffToRemove.id);
        }}
      />
    </>
  );
}
